/* eslint-disable react-hooks/exhaustive-deps */
import { CircularProgress, IconButton, InputAdornment, TextField } from '@material-ui/core';
import { MagnifyingGlassIcon, PencilSimpleIcon, UserPlusIcon } from '@phosphor-icons/react';
import { Switch } from '@orientaction/commons';
import { useAlert } from '@orientaction/hooks';
import { adminService } from '@orientaction/services';
import { getFromLS } from '@orientaction/utils';
import { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import TablePaginationActions from '../../../../components/TableContent/Pagination';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { fetchAssignableStaffUsers } from '../../shared/assignableStaff';
import { getServiceBadgeStyle } from '../../shared/constants';
import { ConsultantSummary } from '../../shared/types';
import { readRole, sortAssignableStaff } from '../../shared/utils';
import {
  BENEFICIARIES_PAGE_SIZE,
  MAX_VISIBLE_PRESTATIONS,
  REASSIGN_BENEFICIARIES_MISMATCH,
  REASSIGN_BENEFICIARIES_SUCCESS,
} from '../constants';
import { useStyles } from '../styles';
import {
  ConsultantBeneficiary,
  ConsultantPagination,
  getRequiredServiceIdsFromBeneficiaries,
  getRequiredServiceIdsFromPrestations,
  mapConsultantBeneficiaries,
  mapConsultantPrestations,
  readPaginationMeta,
  staffHasRequiredServices,
} from '../utils';
import ReassignBeneficiariesModal from './ReassignBeneficiariesModal';

const qs = require('qs');

interface BeneficiariesTabProps {
  user: any;
}

const emptyPagination: ConsultantPagination = {
  page: 1,
  pageSize: BENEFICIARIES_PAGE_SIZE,
  pageCount: 0,
  total: 0,
};

const BeneficiariesTab: FC<BeneficiariesTabProps> = ({ user }) => {
  const classes = useStyles();
  const alert = useAlert();
  const history = useHistory();
  const token = getFromLS('userToken') || '';
  const userConnected = JSON.parse(getFromLS('userInfos') || '{}');
  const consultantId = user?.id;
  const [filterInput, setFilterInput] = useState('');
  const [filter, setFilter] = useState('');
  const [isActivated, setIsActivated] = useState(true);
  const [page, setPage] = useState(1);
  const [beneficiaries, setBeneficiaries] = useState<ConsultantBeneficiary[]>([]);
  const [pagination, setPagination] = useState<ConsultantPagination>(emptyPagination);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignLoading, setReassignLoading] = useState(false);
  const [reassignSaving, setReassignSaving] = useState(false);
  const [eligibleConsultants, setEligibleConsultants] = useState<ConsultantSummary[]>([]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const next = filterInput.trim();
      setFilter(current => {
        if (current !== next) {
          setPage(1);
        }
        return next;
      });
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [filterInput]);

  useEffect(() => {
    if (!consultantId) {
      setBeneficiaries([]);
      setPagination(emptyPagination);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const query = qs.stringify(
          {
            page,
            pageSize: Math.min(BENEFICIARIES_PAGE_SIZE, 100),
            isActivated,
            ...(filter ? { filter } : {}),
          },
          { encodeValuesOnly: true, addQueryPrefix: true }
        );
        const response = await apiServiceNative.get(
          `/api/company-user-admin-consultant-beneficiaries/${consultantId}${query}`
        );
        if (!cancelled) {
          setBeneficiaries(mapConsultantBeneficiaries(response));
          setPagination(
            readPaginationMeta(response, {
              ...emptyPagination,
              page,
            })
          );
        }
      } catch {
        if (!cancelled) {
          setBeneficiaries([]);
          setPagination(emptyPagination);
          alert.openSimpleAlert();
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [consultantId, page, filter, isActivated, reloadKey]);

  const goToBeneficiaryProfile = (beneficiaryId: number) => {
    history.push(`/admin/users-resultats/beneficiaire/${beneficiaryId}`);
  };

  const goToEditPrestation = (item: ConsultantBeneficiary) => {
    const serviceId = item.services[0]?.id;
    if (item.services.length === 1 && serviceId) {
      history.push(`/admin/users-resultats/beneficiaire/${item.id}/prestation/${serviceId}`);
      return;
    }
    history.push(`/admin/users-resultats/beneficiaire/${item.id}?tab=prestations`);
  };

  const closeReassignModal = () => {
    if (reassignSaving) {
      return;
    }
    setReassignOpen(false);
    setEligibleConsultants([]);
  };

  const handleReassign = async () => {
    const companyId = Number(
      user?.company?.id || user?.company?.data?.id || userConnected?.company?.id
    );
    if (!consultantId || !companyId) {
      alert.openSimpleAlert();
      return;
    }

    setReassignOpen(true);
    setReassignLoading(true);
    try {
      const [servicesResponse, staff] = await Promise.all([
        apiServiceNative.get(`/api/company-user-admin-consultant-services/${consultantId}`),
        fetchAssignableStaffUsers({
          companyId,
          consultantOnly: true,
        }),
      ]);
      const requiredIds = Array.from(
        new Set([
          ...getRequiredServiceIdsFromPrestations(mapConsultantPrestations(servicesResponse)),
          ...getRequiredServiceIdsFromBeneficiaries(beneficiaries),
        ])
      );
      setEligibleConsultants(
        sortAssignableStaff(
          staff
            .filter((item: any) => Number(item.id) !== Number(consultantId))
            .filter((item: any) => staffHasRequiredServices(item, requiredIds))
            .map((item: any): ConsultantSummary => {
              const role = readRole(item.company_user_role);
              return {
                id: Number(item.id),
                firstname: item.firstname || '',
                lastname: item.lastname || '',
                company_user_role: role || undefined,
              };
            })
        )
      );
    } catch {
      setEligibleConsultants([]);
      alert.openSimpleAlert();
    } finally {
      setReassignLoading(false);
    }
  };

  const saveReassign = async (nextConsultantId: number) => {
    if (!consultantId || !nextConsultantId || reassignSaving) {
      return;
    }
    setReassignSaving(true);
    try {
      await adminService.reassigned(
        {
          id_for_assigned: consultantId,
          id_current_assigned: nextConsultantId,
        },
        token
      );
      setReassignOpen(false);
      setEligibleConsultants([]);
      setPage(1);
      setReloadKey(current => current + 1);
      alert.setOpenAlert(true, REASSIGN_BENEFICIARIES_SUCCESS, 'success');
    } catch (err: any) {
      if (err?.response?.data?.error?.details?.id === 'transfer_failed') {
        alert.setOpenAlert(true, REASSIGN_BENEFICIARIES_MISMATCH, 'error');
      } else {
        alert.openSimpleAlert();
      }
    } finally {
      setReassignSaving(false);
    }
  };

  return (
    <>
      <div className={classes.beneficiariesToolbar}>
        <TextField
          className={classes.beneficiariesSearch}
          variant="outlined"
          placeholder="Rechercher un nom ou un email"
          value={filterInput}
          onChange={event => setFilterInput(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlassIcon size={20} color="#8B90A5" />
              </InputAdornment>
            ),
          }}
        />
        {/* <Switch
          checked={isActivated}
          onChange={event => {
            setIsActivated(event.target.checked);
            setPage(1);
          }}
          label="Utilisateurs actifs"
          cy="consultant-beneficiaries-actifs"
        /> */}
      </div>

      {loading ? (
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.thLeft}>Bénéficiaires</th>
                  <th className={classes.thLeft}>Prestations</th>
                  <th className={classes.thRight}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {beneficiaries.length === 0 ? (
                  <tr>
                    <td colSpan={3} className={classes.emptyCell}>
                      {filter ? 'Aucun résultat' : 'Aucun bénéficiaire rattaché'}
                    </td>
                  </tr>
                ) : (
                  beneficiaries.map(item => {
                    const fullName =
                      `${item.firstname || ''} ${item.lastname || ''}`.trim() ||
                      item.email ||
                      item.username ||
                      '—';
                    const visibleServices = item.services.slice(0, MAX_VISIBLE_PRESTATIONS);
                    const extraCount = item.services.length - visibleServices.length;
                    return (
                      <tr key={item.id}>
                        <td>
                          <div className={classes.beneficiaryCell}>
                            <button
                              type="button"
                              className={classes.beneficiaryName}
                              onClick={() => goToBeneficiaryProfile(item.id)}>
                              {fullName}
                            </button>
                            {item.email ? (
                              <span className={classes.beneficiaryEmail}>{item.email}</span>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          {item.services.length === 0 ? (
                            '—'
                          ) : (
                            <div className={classes.badgeList}>
                              {visibleServices.map(service => {
                                const colors = getServiceBadgeStyle(Number(service.id));
                                return (
                                  <span
                                    key={service.id}
                                    className={classes.prestationBadge}
                                    style={{
                                      backgroundColor: colors.background,
                                      color: colors.color,
                                    }}>
                                    {service.name}
                                  </span>
                                );
                              })}
                              {extraCount > 0 ? (
                                <span className={classes.overflowBadge}>+{extraCount}</span>
                              ) : null}
                            </div>
                          )}
                        </td>
                        <td className={classes.actionsCell}>
                          <IconButton
                            className={classes.iconBtn}
                            aria-label="Éditer la prestation"
                            onClick={() => goToEditPrestation(item)}>
                            <PencilSimpleIcon size={18} />
                          </IconButton>
                          <IconButton
                            className={classes.iconBtn}
                            aria-label="Réassigner le bénéficiaire"
                            onClick={handleReassign}>
                            <UserPlusIcon size={18} />
                          </IconButton>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div className={classes.tableFooter}>
            <button
              type="button"
              className={classes.reassignBtn}
              onClick={handleReassign}
              disabled={pagination.total === 0 || reassignSaving}>
              <UserPlusIcon size={18} />
              Ré-assigner les bénéficiaires
            </button>
          </div>
          <TablePaginationActions
            count={pagination.total}
            page={Math.max(0, pagination.page - 1)}
            rowsPerPage={pagination.pageSize || BENEFICIARIES_PAGE_SIZE}
            onChangePage={(_event, nextPage) => setPage(nextPage + 1)}
          />
        </>
      )}

      <ReassignBeneficiariesModal
        open={reassignOpen}
        loading={reassignLoading}
        saving={reassignSaving}
        consultants={eligibleConsultants}
        onClose={closeReassignModal}
        onSave={saveReassign}
      />
    </>
  );
};

export default BeneficiariesTab;
